/*
  (c) 2025 Fluxonix Inc.
*/

// src/pages/SignupSuccess.jsx

import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Button,
} from "@mui/material";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthProvider";

export default function SignupSuccess() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("processing"); // "processing" | "otp_pending" | "verified" | "error"
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { login } = useAuth();

  const signupMode = location.state?.signupMode || "trial";

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setError("Missing session ID.");
      return;
    }

    const confirmSignup = async () => {
      try {
        // 1️⃣ Confirm Stripe signup
        const res = await api.post("/api/signup/confirm", null, {
          params: { session_id: sessionId },
        });

        const userEmail = res.data?.email;
        if (!userEmail) throw new Error("Email not returned from signup confirmation.");

        setEmail(userEmail);
        setStatus("otp_pending");

        // 2️⃣ Send OTP automatically
        await api.post("/auth/send-otp", { email: userEmail });
        setOtpSent(true);
      } catch (err) {
        setError(
          err.response?.data?.detail || err.message || "Signup confirmation failed."
        );
        setStatus("error");
      }
    };

    confirmSignup();
  }, [params]);

  // 3️⃣ Verify OTP and issue JWT
  const verifyOtp = async () => {
    try {
      const res = await api.post("/auth/verify-otp", {
        email,
        otp: otpCode,
        signup_mode: signupMode,
      });

      const token = res.data?.access_token;
      if (!token) throw new Error("Token not returned from verification.");

      localStorage.setItem("fluxonix_token", token);

      const userData = res.data?.user;

      // 🎯 Derive roles
      let derivedRoles = [];
      if (userData.role === "b2c_user") {
        derivedRoles = ["admin", "field_agent"];
      } else if (Array.isArray(userData.role)) {
        derivedRoles = userData.role;
      } else if (userData.role) {
        derivedRoles = [userData.role];
      }

      const normalized = {
        ...userData,
        roles: derivedRoles,
        permissions: Array.isArray(userData.permissions)
          ? userData.permissions
          : userData.permissions
          ? [userData.permissions]
          : [],
      };

      // ✅ Persist everything
      localStorage.setItem("fluxonix_user", JSON.stringify(normalized));
      localStorage.setItem("fluxonix_token", token);

      // 🔐 Hydrate auth provider state
      setUser(normalized);
      setToken(token);
      setIsAuthenticated(true);

      setStatus("verified");

      login(normalized, token)

      // ⛳️ Navigate to dashboard
      navigate("/secure/voice");
    } catch (err) {
      console.error("❌ OTP verification failed:", err);
      setError(err.message || "OTP verification failed.");
    }
  };

  return (
    <Box sx={{ p: 4, maxWidth: 480, mx: "auto", textAlign: "center" }}>
      {/* ⏳ Initial Loading */}
      {status === "processing" && !error && (
        <>
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Confirming your Stripe signup...
          </Typography>
        </>
      )}

      {/* ❌ Error */}
      {error && <Alert severity="error">{error}</Alert>}

      {/* 🔐 OTP Pending */}
      {status === "otp_pending" && !error && (
        <Box>
          <Typography variant="h5" gutterBottom>
            🎉 Signup complete!
          </Typography>
          <Typography variant="body1" gutterBottom>
            Please verify your email to activate your Fluxonix workspace.
          </Typography>

          <TextField
            label="Email"
            value={email}
            fullWidth
            margin="normal"
            disabled
          />

          {otpSent && (
            <>
              <TextField
                label="Enter OTP"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
                fullWidth
                margin="normal"
              />
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={verifyOtp}
                sx={{ mt: 2 }}
              >
                Verify & Continue
              </Button>
            </>
          )}
        </Box>
      )}

      {/* ✅ Verified */}
      {status === "verified" && (
        <Typography variant="h5" sx={{ mt: 2 }}>
          ✅ Email verified! Redirecting to your dashboard...
        </Typography>
      )}
    </Box>
  );
}
